import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { ReadMore } from "@seek/profile-read-ui";
import { useTranslations } from "@vocab/react";
import { Box, Stack, Heading, Inline, Rating, Text, TextLink, IconCompany, IconPeople, IconGift, Actions, ButtonLink, IconArrow } from "braid-design-system";
import { useInView } from "react-intersection-observer";
import { useAnalyticsHandlers } from "../CompanyName/CompanyName.mjs";
import { translations } from "../../../components/CompanyProfile/.vocab/index.mjs";
import { perksAndBenefits } from "./CompanyProfile.css.mjs";
const LOGO_MAX_HEIGHT = 50;
const CompanyProfile = (company) => {
  const {
    logo,
    profileUrl,
    reviewsUrl,
    name,
    onAnalyticsEvent = () => {
    },
    overview,
    perksAndBenefits: perksAndBenefits$1 = [],
    overallRating,
    shouldDisplayReviews = false
  } = company;
  const industry = overview == null ? void 0 : overview.industry;
  const companySize = overview == null ? void 0 : overview.companySize;
  const companyDescription = (overview == null ? void 0 : overview.companyDescription) ?? [];
  const { t } = useTranslations(translations);
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      if (inView) {
        analytics.onCompanyProfileWidgetImpression();
      }
    },
    threshold: 0.5,
    triggerOnce: true
  });
  const showProfile = company && name && profileUrl;
  return showProfile && /* @__PURE__ */ jsx(Box, { ref: trackedComponent, children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(Heading, { level: "3", children: t("Company profile") }),
    /* @__PURE__ */ jsxs(
      Box,
      {
        borderRadius: "xlarge",
        boxShadow: "borderNeutralLight",
        data: { automation: "company-profile" },
        padding: "large",
        children: [
          /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
            /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              Boolean(logo) && /* @__PURE__ */ jsx(Box, { display: "flex", height: "full", children: /* @__PURE__ */ jsx(
                Box,
                {
                  component: "img",
                  src: logo,
                  alt: `Company Logo for ${name}`,
                  style: {
                    maxHeight: LOGO_MAX_HEIGHT
                  }
                }
              ) }),
              /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                Boolean(name) && /* @__PURE__ */ jsx(Heading, { level: "4", children: name }),
                shouldDisplayReviews && overallRating && overallRating.rating > 0 && overallRating.reviewCount > 0 && Boolean(reviewsUrl) && /* @__PURE__ */ jsxs(
                  Inline,
                  {
                    space: "xxsmall",
                    alignY: "bottom",
                    data: { automation: "company-profile-review" },
                    children: [
                      /* @__PURE__ */ jsx(
                        Rating,
                        {
                          rating: overallRating.rating,
                          variant: "minimal",
                          data: {
                            automation: "company-profile-review-rating"
                          }
                        }
                      ),
                      reviewsUrl != null && /* @__PURE__ */ jsxs(Fragment, { children: [
                        /* @__PURE__ */ jsx(Text, { children: "·" }),
                        /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
                          TextLink,
                          {
                            href: reviewsUrl,
                            rel: "nofollow",
                            weight: "weak",
                            data: {
                              automation: "company-profile-review-link"
                            },
                            onClick: analytics.onCompanyProfileWidgetReviewsClick,
                            children: t("{companyReviewsCount} reviews", {
                              companyReviewsCount: overallRating.reviewCount.toString()
                            })
                          }
                        ) })
                      ] })
                    ]
                  }
                )
              ] })
            ] }) }) }),
            (Boolean(industry) || Boolean(companySize)) && /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
              Boolean(industry) && /* @__PURE__ */ jsx(Text, { size: "standard", icon: /* @__PURE__ */ jsx(IconCompany, {}), children: industry }),
              Boolean(companySize) && /* @__PURE__ */ jsx(Text, { size: "standard", icon: /* @__PURE__ */ jsx(IconPeople, {}), children: companySize })
            ] }) }),
            companyDescription && companyDescription.length > 0 && /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsx(
              ReadMore,
              {
                moreLabel: t("Show more"),
                lessLabel: t("Show less"),
                linesToPreview: 3,
                onShowMore: analytics.onCompanyProfileWidgetAccordionClick,
                children: /* @__PURE__ */ jsx(Stack, { space: "small", children: companyDescription.map((section, index) => /* @__PURE__ */ jsx(Box, { component: "p", children: section }, `companyDescription-${index}`)) })
              }
            ) })
          ] }),
          perksAndBenefits$1 && perksAndBenefits$1.length > 0 && /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(Box, { component: "section", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsx(Text, { weight: "strong", icon: /* @__PURE__ */ jsx(IconGift, {}), children: t("Perks and benefits") }),
            /* @__PURE__ */ jsx(Inline, { space: "medium", collapseBelow: "tablet", children: perksAndBenefits$1.map((perk, index) => /* @__PURE__ */ jsx(Text, { size: "standard", children: /* @__PURE__ */ jsx(Box, { className: perksAndBenefits, children: perk }) }, `${perk}-${index}`)) })
          ] }) }) }),
          Boolean(profileUrl) && /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
            ButtonLink,
            {
              href: profileUrl,
              onClick: analytics.onCompanyProfileWidgetClick,
              data: { automation: "company-profile-profile-link" },
              icon: /* @__PURE__ */ jsx(IconArrow, { direction: "right" }),
              iconPosition: "trailing",
              children: t("More about this company")
            }
          ) }) })
        ]
      }
    )
  ] }) });
};
export {
  CompanyProfile
};
